import {
	OktaAuth,
	OktaAuthOptions,
	TokenResponse,
	Tokens,
} from '@okta/okta-auth-js';
import { autoinject } from 'aurelia-framework';
import { StateHistory, Store } from 'aurelia-store';

import { AppState } from '~/app-store';
import { LoginActions } from '~/login/login-actions';
import { SettingsState } from '~/settings-state/settings-state';
import { getItem, removeItem } from '~/utils/local-storage';
import { Logger } from '~/utils/logger';

/**
 * The name of the session variable that OktaAuth TokenManager uses to store Access and ID tokens
 */
export const OKTA_TOKEN_STORAGE: string = 'okta-token-storage';
/**
 * The name of the session variable that stores the URL prior to redirecting to Okta login
 */
export const OKTA_PRE_REDIRECT_URL: string = 'okta-pre-redirect-url';

@autoinject
export class OktaLogin {
	/**
	 * The name of the session variable that stores the Okta Auth Options from the Global Settings API
	 */
	private static readonly OKTA_AUTH_OPTIONS: string = 'okta-auth-options';

	constructor(
		private readonly settingsState: SettingsState,
		private readonly store: Store<StateHistory<AppState>>,
	) {}

	/**
	 * Check Session Storage for any Okta Auth Options. If none exist, try to fetch these from the Global Settings API.
	 * If these are present and valid, save them to Session Storage and return them as an {@link OktaAuthOptions} object.
	 */
	public async getOktaAuthOptions(): Promise<OktaAuthOptions> {
		let oktaAuthOptions: Readonly<OktaAuthOptions> =
			OktaLogin.getOktaAuthOptionsFromStorage();
		if (oktaAuthOptions) {
			return oktaAuthOptions;
		} else {
			oktaAuthOptions = this.settingsState.state.oktaAuthOptions;
			if (oktaAuthOptions) {
				if (oktaAuthOptions.redirectUri && oktaAuthOptions.scopes) {
					sessionStorage.setItem(
						OktaLogin.OKTA_AUTH_OPTIONS,
						JSON.stringify(oktaAuthOptions),
					);
					return oktaAuthOptions;
				}
				Logger.error(
					'Okta config is enabled but Redirect URI or Scope is invalid. Use default login instead.',
				);
			}
		}
		return undefined;
	}

	private static getOktaAuthOptionsFromStorage():
		| Readonly<OktaAuthOptions>
		| undefined {
		const oktaAuthOptionsValue = sessionStorage.getItem(
			OktaLogin.OKTA_AUTH_OPTIONS,
		);
		if (oktaAuthOptionsValue && oktaAuthOptionsValue !== '{}') {
			return JSON.parse(oktaAuthOptionsValue);
		}
		return undefined;
	}

	private async getOktaAuthClient(): Promise<OktaAuth> {
		const oktaAuthOptions: OktaAuthOptions =
			await this.getOktaAuthOptions();
		return new OktaAuth(oktaAuthOptions);
	}

	/**
	 * User not logged in yet so redirect from app to Okta login using Okta Auth Options
	 */
	public async redirectToOkta(
		encodedUrl: string = window.location.href,
	): Promise<void> {
		const oktaAuthOptions: Readonly<OktaAuthOptions> =
			await this.getOktaAuthOptions();
		const authClient: OktaAuth = new OktaAuth(oktaAuthOptions);
		if (!authClient.isLoginRedirect()) {
			sessionStorage.setItem(OKTA_PRE_REDIRECT_URL, encodedUrl);
			await authClient.token.getWithRedirect({
				scopes: oktaAuthOptions.scopes,
			});
		}
	}

	/**
	 * Check Session Storage for Okta Auth Options. If attempt is a redirect
	 * from app to Okta and back to app, try to parse tokens from URL.
	 * Otherwise, read tokens without prompt as Okta session may already exists.
	 * Any time we attempt an Okta login, any old tokens become invalid
	 * and must be removed before handling the newly requested tokens.
	 */
	public async attemptOktaLogin(): Promise<void> {
		removeItem(OKTA_TOKEN_STORAGE);
		const authClient: OktaAuth = await this.getOktaAuthClient();

		let tokenResponse: TokenResponse;
		try {
			if (authClient.isLoginRedirect()) {
				Logger.log('Attempting to parse token from URL');
				tokenResponse = await authClient.token.parseFromUrl();
			} else {
				Logger.log(
					'Attempting to get token from cookies on the Okta domain via an iFrame',
				);
				tokenResponse = await authClient.token.getWithoutPrompt();
			}
		} catch (e) {
			Logger.error('Failed to get token response', e);
			Logger.log('Attempting to get token with a popup');
			tokenResponse = await authClient.token.getWithPopup();
		}
		// TokenManager saves tokens to Local Storage by default under key 'okta-token-storage'
		authClient.tokenManager.setTokens(tokenResponse.tokens);
		this.store.dispatch(LoginActions.LoginUser);
	}

	/**
	 * This simply checks if user is logged in through Okta according to presence of Okta Token Storage.
	 * Tokens should not be directly retrieved from storage as we cannot verify the validity of the token.
	 * To get valid and non-expired tokens, use {@link getAccessToken()} to read them from Token Manager.
	 */
	public static isLoggedIn(): boolean {
		const oktaTokenStorage: string = getItem(OKTA_TOKEN_STORAGE);
		return oktaTokenStorage && oktaTokenStorage !== '{}';
	}

	/**
	 * Get the Access Token from the Okta Token Manager. By using the Token Manager, this ensures that
	 * token expiration and renew operations are correctly managed. When a token is added to the tokenManager,
	 * it is automatically renewed when it expires.
	 */
	public static async getAccessToken(): Promise<string | undefined> {
		const oktaAuthOptions: Readonly<OktaAuthOptions> =
			this.getOktaAuthOptionsFromStorage();
		if (oktaAuthOptions) {
			const authClient: OktaAuth = new OktaAuth(oktaAuthOptions);
			const tokens: Tokens = await authClient.tokenManager.getTokens();
			if (tokens?.accessToken?.accessToken) {
				return tokens.accessToken.accessToken;
			}
		}
		return undefined;
	}

	public async logout(): Promise<void> {
		removeItem(OKTA_TOKEN_STORAGE);
		const authClient: OktaAuth = await this.getOktaAuthClient();
		await authClient.revokeAccessToken();
		authClient.tokenManager.clear();
	}

	/**
	 * Checks for the presence of Okta Auth Options in Session Storage.
	 */
	public static oktaAuthOptionsAreSet(): boolean {
		const oktaAuthOptions = sessionStorage.getItem(
			OktaLogin.OKTA_AUTH_OPTIONS,
		);
		return oktaAuthOptions && oktaAuthOptions !== '{}';
	}
}
